import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, User as UserIcon, Building2, Shield, Activity, CreditCard } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { User } from '@/_types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Section = 'overview' | 'organization' | 'roles' | 'activity' | 'billing';

interface NavItem {
  id: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { id: 'overview', label: 'Overview', icon: UserIcon },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'roles', label: 'Roles', icon: Shield },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

// ---------------------------------------------------------------------------
// Helper: format date
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Section: Overview
// ---------------------------------------------------------------------------

function OverviewSection({ user }: { user: User }) {
  const authSourceBadge = (src: User['auth_source']) => {
    if (src === 'google')
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">Google</Badge>
      );
    if (src === 'microsoft')
      return (
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
          Microsoft
        </Badge>
      );
    return <Badge variant="secondary">Normal</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold mb-4">Overview</h3>
        <dl className="space-y-4">
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Full Name</dt>
            <dd className="text-sm font-medium">{user.name}</dd>
          </div>
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Email</dt>
            <dd className="text-sm font-medium">{user.email}</dd>
          </div>
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Auth Source</dt>
            <dd>{authSourceBadge(user.auth_source)}</dd>
          </div>
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Status</dt>
            <dd>
              {user.deleted_at ? (
                <Badge variant="destructive">Deleted</Badge>
              ) : (
                <Badge variant={user.is_active ? 'success' : 'secondary'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </dd>
          </div>
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Created At</dt>
            <dd className="text-sm">{formatDate(user.created_at)}</dd>
          </div>
          <div className="flex items-start gap-4">
            <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Last Login</dt>
            <dd className="text-sm">{formatDate(user.last_login_at)}</dd>
          </div>
          {user.deleted_at && (
            <div className="flex items-start gap-4">
              <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Deleted At</dt>
              <dd className="text-sm text-destructive">{formatDate(user.deleted_at)}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Organization
// ---------------------------------------------------------------------------

function OrganizationSection({ user }: { user: User }) {
  if (!user.organization) {
    return (
      <div>
        <h3 className="text-base font-semibold mb-4">Organization</h3>
        <p className="text-sm text-muted-foreground italic">
          This user is not associated with any organization (system user).
        </p>
      </div>
    );
  }

  const { organization } = user;

  return (
    <div>
      <h3 className="text-base font-semibold mb-4">Organization</h3>
      <dl className="space-y-4">
        <div className="flex items-start gap-4">
          <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Name</dt>
          <dd className="text-sm font-medium">{organization.name}</dd>
        </div>
        <div className="flex items-start gap-4">
          <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Slug</dt>
          <dd className="text-sm font-mono text-muted-foreground">{organization.slug}</dd>
        </div>
        <div className="flex items-start gap-4">
          <dt className="w-36 flex-shrink-0 text-sm text-muted-foreground">Plan</dt>
          <dd>
            {organization.plan ? (
              <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 capitalize">
                {organization.plan}
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Roles
// ---------------------------------------------------------------------------

function RolesSection({ user }: { user: User }) {
  const orgRoleBadgeClass = (role: string) => {
    if (role === 'owner') return 'bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 capitalize';
    if (role === 'admin') return 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 capitalize';
    return 'capitalize';
  };

  return (
    <div className="space-y-6">
      {/* Organisation Role */}
      {user.organization && (
        <div>
          <h3 className="text-base font-semibold mb-3">Organisation Role</h3>
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
            <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">{user.organization.name}</p>
              {user.org_role ? (
                <Badge className={`mt-1 ${orgRoleBadgeClass(user.org_role)}`}>
                  {user.org_role}
                </Badge>
              ) : (
                <p className="text-xs text-muted-foreground italic">No org role</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* System Roles */}
      <div>
        <h3 className="text-base font-semibold mb-3">System Roles</h3>
        {user.roles.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No system roles assigned.</p>
        ) : (
          <div className="space-y-3">
            {user.roles.map((role) => (
              <div
                key={role.id}
                className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3"
              >
                <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">{role.title}</p>
                  <p className="text-xs text-muted-foreground font-mono">{role.slug}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Activity
// ---------------------------------------------------------------------------

interface Session {
  id: number;
  device_name: string;
  ip_address: string | null;
  user_agent: string | null;
  last_used_at: string | null;
  created_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

function ActivitySection({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-user-activity', userId, page],
    queryFn: async () => {
      const res = await apiClient.get(`/users/${userId}/activity`, {
        params: { page, per_page: 20 },
      });
      return {
        sessions: (res.data?.data?.sessions ?? []) as Session[],
        meta: res.data?.meta as { current_page: number; last_page: number; total: number } | undefined,
      };
    },
  });

  const sessions = data?.sessions ?? [];
  const meta = data?.meta;

  return (
    <div>
      <h3 className="text-base font-semibold mb-4">Login Sessions</h3>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No login sessions recorded.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <div
              key={s.id}
              className="flex items-start gap-3 rounded-lg border bg-muted/30 px-4 py-3"
            >
              <Activity className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate">{s.device_name}</p>
                  <Badge
                    variant={s.is_active ? 'success' : 'secondary'}
                    className="text-[10px] px-1.5 py-0"
                  >
                    {s.is_active ? 'Active' : 'Expired'}
                  </Badge>
                </div>
                {s.ip_address && (
                  <p className="text-xs text-muted-foreground font-mono">{s.ip_address}</p>
                )}
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>Signed in: {formatDate(s.created_at)}</span>
                  {s.last_used_at && (
                    <span>Last active: {formatDate(s.last_used_at)}</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                Page {meta.current_page} of {meta.last_page} · {meta.total} sessions total
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-xs px-2 py-1 rounded border disabled:opacity-40 hover:bg-accent"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= meta.last_page}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-xs px-2 py-1 rounded border disabled:opacity-40 hover:bg-accent"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Billing
// ---------------------------------------------------------------------------

interface BillingData {
  no_organization: boolean;
  subscription?: {
    plan: string | null;
    plan_interval: string;
    plan_status: string | null;
    trial_ends_at: string | null;
    subscription_ends_at: string | null;
  };
  credits?: {
    lead_credits_remaining: number;
    lead_credits_total: number;
    email_credits_remaining: number;
    email_credits_total: number;
    is_unlimited: boolean;
  };
  transactions?: {
    id: string;
    type?: string;
    plan_slug: string | null;
    amount: string;
    currency: string;
    status: string;
    lead_credits_granted?: number | null;
    email_credits_granted?: number | null;
    created_at: string;
  }[];
}

function BillingSection({ userId, hasOrg }: { userId: string; hasOrg: boolean }) {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['admin-user-billing', userId],
    queryFn: async () => {
      const res = await apiClient.get(`/users/${userId}/billing`);
      return (res.data?.data ?? res.data) as BillingData;
    },
    enabled: hasOrg,
  });

  if (!hasOrg) {
    return (
      <div>
        <h3 className="text-base font-semibold mb-4">Billing</h3>
        <p className="text-sm text-muted-foreground italic">
          This user has no organization — billing data is unavailable.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <h3 className="text-base font-semibold mb-4">Billing</h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Loading…
        </div>
      </div>
    );
  }

  const { subscription, credits, transactions = [] } = raw ?? {};

  const dateLabel = (() => {
    if (!subscription) return null;
    if (subscription.plan_status === 'trialing' && subscription.trial_ends_at) {
      return `Trial ends ${new Date(subscription.trial_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (subscription.plan_status === 'cancelled' && subscription.subscription_ends_at) {
      return `Access ends ${new Date(subscription.subscription_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (subscription.subscription_ends_at) {
      return `Next billing date: ${new Date(subscription.subscription_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return null;
  })();

  const leadUsed = credits ? Math.max(0, credits.lead_credits_total - credits.lead_credits_remaining) : 0;
  const leadPct = credits && credits.lead_credits_total > 0 ? Math.min(100, (leadUsed / credits.lead_credits_total) * 100) : 0;
  const leadOverLimit = credits && !credits.is_unlimited && credits.lead_credits_remaining < 0;

  const emailUsed = credits ? Math.max(0, credits.email_credits_total - credits.email_credits_remaining) : 0;
  const emailPct = credits && credits.email_credits_total > 0 ? Math.min(100, (emailUsed / credits.email_credits_total) * 100) : 0;
  const emailOverLimit = credits && !credits.is_unlimited && credits.email_credits_remaining < 0;

  return (
    <div className="space-y-6">
      <h3 className="text-base font-semibold">Billing</h3>

      {/* Current Plan */}
      <div className="rounded-xl border bg-muted/30 p-4 space-y-1">
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 mb-2">
          Current Plan
        </Badge>
        <p className="text-sm font-semibold">
          {subscription?.plan
            ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)
            : 'Trial'}{' '}
          Plan
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm text-muted-foreground">Status:</span>
          {subscription?.plan_status ? (
            <Badge
              variant={
                subscription.plan_status === 'active'
                  ? 'success'
                  : subscription.plan_status === 'trialing'
                  ? 'secondary'
                  : subscription.plan_status === 'cancelled'
                  ? 'destructive'
                  : 'outline'
              }
              className="capitalize text-xs"
            >
              {subscription.plan_status}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
        {dateLabel && <p className="text-sm text-muted-foreground">{dateLabel}</p>}
      </div>

      {/* Credit Usage */}
      <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
        <p className="text-sm font-semibold">Credit Usage</p>
        <div className="grid grid-cols-2 gap-4">
          {/* Lead Credits */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Lead Credits</span>
              <span className="text-xs font-medium">
                {credits
                  ? credits.is_unlimited
                    ? '∞ Unlimited'
                    : `${leadUsed.toLocaleString()} / ${credits.lead_credits_total.toLocaleString()}`
                  : '—'}
                {leadOverLimit && (
                  <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">Over limit</Badge>
                )}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: credits ? (credits.is_unlimited ? '100%' : `${leadPct}%`) : '0%',
                  backgroundColor: leadOverLimit ? '#E53935' : '#0ea5e9',
                }}
              />
            </div>
          </div>
          {/* Email Credits */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Email Credits</span>
              <span className="text-xs font-medium">
                {credits
                  ? credits.is_unlimited
                    ? '∞ Unlimited'
                    : `${emailUsed.toLocaleString()} / ${credits.email_credits_total.toLocaleString()}`
                  : '—'}
                {emailOverLimit && (
                  <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">Over limit</Badge>
                )}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: credits ? (credits.is_unlimited ? '100%' : `${emailPct}%`) : '0%',
                  backgroundColor: emailOverLimit ? '#E53935' : '#f59e0b',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
        <p className="text-sm font-semibold">Billing History</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-2 text-left text-xs font-medium text-muted-foreground">Date</th>
                <th className="pb-2 text-left text-xs font-medium text-muted-foreground">Description</th>
                <th className="pb-2 text-left text-xs font-medium text-muted-foreground">Credits</th>
                <th className="pb-2 text-right text-xs font-medium text-muted-foreground">Amount</th>
                <th className="pb-2 text-right text-xs font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-xs text-muted-foreground italic">
                    No transactions yet.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id} className="border-b last:border-0">
                    <td className="py-2 text-xs">
                      {tx.created_at
                        ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="py-2 text-xs">
                      {tx.type === 'credit_purchase' ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200 text-[10px] px-1.5 py-0">
                          Credit Purchase
                        </Badge>
                      ) : (
                        <span className="capitalize">{tx.plan_slug ?? '—'} Plan</span>
                      )}
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {tx.type === 'credit_purchase' ? (
                        <span>
                          {tx.lead_credits_granted ? `${tx.lead_credits_granted}L` : ''}
                          {tx.lead_credits_granted && tx.email_credits_granted ? ' + ' : ''}
                          {tx.email_credits_granted ? `${tx.email_credits_granted}E` : ''}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2 text-xs text-right">
                      ${parseFloat(tx.amount).toFixed(2)} {tx.currency.toUpperCase()}
                    </td>
                    <td className="py-2 text-right">
                      <Badge
                        variant={tx.status === 'succeeded' ? 'success' : tx.status === 'failed' ? 'destructive' : tx.status === 'pending' ? 'warning' : 'secondary'}
                        className="text-[10px] px-1.5 py-0 capitalize"
                      >
                        {tx.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserDetailPage
// ---------------------------------------------------------------------------

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>('overview');

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-user-detail', id],
    queryFn: async () => {
      const res = await apiClient.get(`/users/${id}`);
      return (res.data?.data?.user ?? res.data?.data) as User;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Users
        </Button>
        <p className="text-sm text-muted-foreground">User not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      {/* Settings-style layout */}
      <div className="flex gap-6">
        {/* Left sidebar nav */}
        <aside className="w-48 flex-shrink-0">
          <nav className="space-y-0.5">
            {navItems.map(({ id: sectionId, label, icon: Icon }) => (
              <button
                key={sectionId}
                type="button"
                onClick={() => setActiveSection(sectionId)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                  activeSection === sectionId
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
              </button>
            ))}
          </nav>

          {/* Quick actions */}
          <div className="mt-6 space-y-2">
            <p className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
              Actions
            </p>
            {!user.deleted_at && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate(`/users/${id}/edit`)}
              >
                Edit User
              </Button>
            )}
          </div>
        </aside>

        {/* Right content area */}
        <div className="flex-1 rounded-xl border bg-card p-6 min-h-[400px]">
          {activeSection === 'overview' && <OverviewSection user={user} />}
          {activeSection === 'organization' && <OrganizationSection user={user} />}
          {activeSection === 'roles' && <RolesSection user={user} />}
          {activeSection === 'activity' && <ActivitySection userId={id!} />}
          {activeSection === 'billing' && <BillingSection userId={id!} hasOrg={!!user.organization} />}
        </div>
      </div>
    </div>
  );
}
